import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ChatMensaje } from '../models/chat.model';
import { ChatHubService } from './chat-hub.service';
import { PushService } from './push.service';

/** Notificación lista para mostrar — el toast component la consume directamente. */
export interface ChatNotificacion {
  id: number;
  conversacionId: number;
  remitenteId: number;
  remitenteNombre: string;
  preview: string;
  enviadoEn: Date;
}

const ORIGINAL_TITLE_KEY = 'crm-original-title';
const SOUND_PREF_KEY = 'crm.chat.notif.sound';
const PUSH_PREF_KEY = 'crm.chat.notif.push';

/**
 * Notificaciones de chat al estilo Teams/Slack — escuchando al `ChatHubService`.
 *
 * Se autoinicializa: en cuanto se inyecta arranca la conexión SignalR para que
 * los mensajes lleguen incluso cuando el usuario NO está en `/chat`. Esto hace
 * que el toast + badge favicon + Browser Notification funcionen globalmente.
 *
 * Suprime la notificación si el usuario está en `/chat` con la conversación
 * abierta (no notificamos algo que ya está viendo).
 */
@Injectable({ providedIn: 'root' })
export class ChatNotificationsService {
  private hub = inject(ChatHubService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private doc = inject(DOCUMENT);
  private push = inject(PushService);

  // === Estado interno ===
  private originalTitle = '';
  private titleFlashTimer: ReturnType<typeof setInterval> | null = null;
  private titleFlashState = false;
  private audioCtx: AudioContext | null = null;
  private currentChatRoute = false;
  /** Conversación abierta en /chat — si es esta, no notificamos. */
  private currentConversacionId: number | null = null;

  // === Outputs ===

  readonly notifications$ = new Subject<ChatNotificacion>();

  readonly browserPermission = signal<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied',
  );

  readonly soundEnabled = signal<boolean>(this.readPref(SOUND_PREF_KEY, true));
  readonly pushEnabled = signal<boolean>(this.readPref(PUSH_PREF_KEY, true));

  /** Contador de no-leídos local. Acumula mensajes que el user no está viendo. */
  readonly noLeidosPorConversacion = signal<Map<number, number>>(new Map());
  readonly totalNoLeidos = computed(() => {
    let total = 0;
    for (const v of this.noLeidosPorConversacion().values()) total += v;
    return total;
  });

  constructor() {
    this.originalTitle = this.doc.title;
    try { sessionStorage.setItem(ORIGINAL_TITLE_KEY, this.originalTitle); } catch { /* ignore */ }

    // Auto-start del hub apenas el usuario esté logueado.
    // Caso 1: F5 con sesión existente → AppComponent monta → este constructor
    //   corre con token en localStorage → maybeStartHub conecta.
    // Caso 2: login fresco (sin F5) → AppComponent ya estaba montado en /login;
    //   AuthService emite `loggedIn$` post-login → reaccionamos acá y arrancamos
    //   el hub. Sin este suscribe, el user aparece offline en backend hasta
    //   que entra manualmente a /chat.
    this.maybeStartHub();
    this.auth.loggedIn$.subscribe(() => this.maybeStartHub());

    // Logout (manual o auto por expirar JWT): cerrar la conexión SignalR
    // limpiamente para que el backend marque al user como offline en
    // `PresenceTracker` inmediatamente. Sin esto, el user aparecía online
    // hasta que el server detectara el timeout de la conexión (~minutos).
    this.auth.loggedOut$.subscribe(() => void this.hub.stop());

    // Listener: mensajes nuevos del hub.
    this.hub.mensajeRecibido$.subscribe(m => this.onMensajeRecibido(m));
    // Si el server confirma "leído" de mensajes que enviamos, no afecta noLeidos
    // del lado del caller — pero podría afectar UI. Ver más adelante.

    // Persiste prefs.
    effect(() => { this.writePref(SOUND_PREF_KEY, this.soundEnabled()); });
    effect(() => { this.writePref(PUSH_PREF_KEY, this.pushEnabled()); });

    // Tab title con contador de no-leídos.
    effect(() => {
      const total = this.totalNoLeidos();
      this.refreshTabTitle(total);
    });

    // Ruta: trackear si está en /chat.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.currentChatRoute = e.urlAfterRedirects.startsWith('/chat');
      });

    this.doc.addEventListener('visibilitychange', () => {
      if (this.doc.visibilityState === 'visible') this.stopTitleFlash();
    });
  }

  /**
   * El componente chat informa qué conversación está activa y si el usuario
   * la está MIRANDO activamente. Si `viewing = false`, la notificación NO se
   * suprime aunque la conversación esté seleccionada (caso: estás en sidebar
   * o leyendo histórico arriba — no estás viendo los mensajes nuevos).
   */
  setActiveConversacion(id: number | null, viewing: boolean = true) {
    this.currentConversacionId = viewing ? id : null;
    if (id !== null && viewing) {
      // Limpiar el contador de esa conversación cuando se abre y se mira.
      this.noLeidosPorConversacion.update(map => {
        if (!map.has(id)) return map;
        const next = new Map(map);
        next.delete(id);
        return next;
      });
    }
  }

  async requestBrowserPermission(): Promise<NotificationPermission> {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission !== 'default') {
      this.browserPermission.set(Notification.permission);
      return Notification.permission;
    }
    try {
      const result = await Notification.requestPermission();
      this.browserPermission.set(result);
      return result;
    } catch {
      this.browserPermission.set('denied');
      return 'denied';
    }
  }

  toggleSound() { this.soundEnabled.update(v => !v); }

  /**
   * Toggle de notificaciones del navegador (in-tab toasts) + Web Push del SO.
   * - Apagar: marca el flag local y desuscribe del Web Push (el endpoint queda
   *   borrado en backend y el browser deja de recibir push del SO).
   * - Prender: pide permission si todavía no fue otorgada Y registra la
   *   subscription Web Push para que funcione con app cerrada.
   */
  togglePush() {
    if (this.pushEnabled()) {
      this.pushEnabled.set(false);
      void this.push.unsubscribe();
      return;
    }
    if (this.browserPermission() !== 'granted') void this.requestBrowserPermission();
    this.pushEnabled.set(true);
    void this.push.subscribe();
  }

  // ========== Internals ==========

  private maybeStartHub(): void {
    // Solo arrancamos si hay sesión activa. Si no, esperamos al próximo login.
    if (!this.auth.accessToken) return;
    void this.hub.start().catch(err => {
      console.warn('[ChatNotifications] no se pudo iniciar el hub:', err);
    });
  }

  private onMensajeRecibido(m: ChatMensaje): void {
    const yoId = this.auth.user?.id ?? 0;
    // Ignoramos mensajes que YO mandé (rebote del server al caller).
    if (m.remitenteId === yoId) return;
    if (m.eliminado) return;

    // Determinar la "otra punta" de la conversación = el remitente para mí.
    const otroId = m.remitenteId;

    // Suprimir si está viendo la conversación.
    const usuarioLoEstaViendo =
      this.currentChatRoute &&
      this.currentConversacionId === m.conversacionId &&
      this.doc.visibilityState === 'visible';

    if (!usuarioLoEstaViendo) {
      // Incrementar contador.
      this.noLeidosPorConversacion.update(map => {
        const next = new Map(map);
        next.set(otroId, (next.get(otroId) ?? 0) + 1);
        return next;
      });

      const notif: ChatNotificacion = {
        id: m.id,
        conversacionId: m.conversacionId,
        remitenteId: m.remitenteId,
        remitenteNombre: m.remitenteNombre ?? 'Mensaje',
        preview: (m.texto || '').slice(0, 140),
        enviadoEn: m.enviadoEn,
      };

      this.notifications$.next(notif);

      if (this.soundEnabled()) this.playPing();
      if (this.pushEnabled() && this.doc.visibilityState !== 'visible') {
        this.showBrowserNotification(notif);
      }
    }
  }

  private showBrowserNotification(notif: ChatNotificacion) {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(notif.remitenteNombre, {
        body: notif.preview,
        icon: 'assets/img/ionics-logo.gif',
        tag: `chat-${notif.conversacionId}`,
        renotify: false,
        silent: true,
      } as NotificationOptions);
      n.onclick = () => {
        window.focus();
        this.router.navigate(['/chat'], { queryParams: { u: notif.remitenteId } });
        n.close();
      };
      setTimeout(() => { try { n.close(); } catch { /* ignore */ } }, 8000);
    } catch { /* ignore */ }
  }

  private playPing() {
    try {
      this.audioCtx ??= new (window.AudioContext || (window as any).webkitAudioContext)();
      const ctx = this.audioCtx;
      const now = ctx.currentTime;
      const playTone = (freq: number, start: number, duration: number, volume: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + start);
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(volume, now + start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + duration + 0.05);
      };
      playTone(659.25, 0,    0.18, 0.18);
      playTone(1046.5, 0.10, 0.20, 0.14);
    } catch { /* autoplay bloqueado */ }
  }

  private refreshTabTitle(total: number) {
    if (total <= 0) {
      this.stopTitleFlash();
      this.doc.title = this.originalTitle;
      return;
    }
    const flashedTitle = `(${total}) Mensaje${total === 1 ? '' : 's'} nuevo${total === 1 ? '' : 's'}`;
    const baseTitle = `${flashedTitle} · ${this.originalTitle}`;
    this.doc.title = baseTitle;
    if (this.doc.visibilityState !== 'visible') this.startTitleFlash(flashedTitle, baseTitle);
  }

  private startTitleFlash(alt: string, base: string) {
    this.stopTitleFlash();
    this.titleFlashState = false;
    this.titleFlashTimer = setInterval(() => {
      this.titleFlashState = !this.titleFlashState;
      this.doc.title = this.titleFlashState ? alt : base;
    }, 1500);
  }

  private stopTitleFlash() {
    if (this.titleFlashTimer) {
      clearInterval(this.titleFlashTimer);
      this.titleFlashTimer = null;
    }
  }

  private readPref(key: string, defaultValue: boolean): boolean {
    try {
      const v = localStorage.getItem(key);
      if (v == null) return defaultValue;
      return v === '1';
    } catch { return defaultValue; }
  }

  private writePref(key: string, value: boolean) {
    try { localStorage.setItem(key, value ? '1' : '0'); } catch { /* ignore */ }
  }
}
